/* SS - Testimonials #28: loop slider + video toggles.
 * Self-contained re-implementation of what the original did with Swiper 6.8.4
 * (loop: true, speed: 300, navigation, breakpoints 320/768/1024 from data-* attributes)
 * plus its click-to-play video handling. One <t28-slider> per section instance. */
(() => {
  if (customElements.get('t28-slider')) return;

  const SPEED = 300;
  const MQ_MOBILE = '(min-width: 320px)';
  const MQ_DESKTOP = '(min-width: 768px)';
  const FOCUSABLE = 'input, select, option, textarea, button, video, label';

  class T28Slider extends HTMLElement {
    connectedCallback() {
      if (this.track) return;
      this.track = this.querySelector('.t28__tr');
      if (!this.track) return;
      const root = this.closest('.t28') || this.parentElement;
      this.prevEl = root.querySelector('.t28__pv');
      this.nextEl = root.querySelector('.t28__nx');

      // The original rendered every slide block four times; reproduce that set client-side.
      const originals = [...this.track.children].filter((el) => el.classList.contains('t28__s'));
      if (!originals.length) return;
      const frag = document.createDocumentFragment();
      for (let k = 1; k < 4; k += 1) originals.forEach((el) => frag.appendChild(el.cloneNode(true)));
      this.track.appendChild(frag);
      const real = [...this.track.children];
      real.forEach((el, i) => {
        el.dataset.i = i;
        el.setAttribute('role', 'group');
        el.setAttribute('aria-label', `${i + 1} / ${real.length}`);
      });
      this.realCount = real.length;
      const trackId = `t28-wrapper-${Math.random().toString(16).slice(2, 10)}`;
      this.track.id = trackId;
      [this.prevEl, this.nextEl].forEach((btn) => btn && btn.setAttribute('aria-controls', trackId));

      this.tx = 0;
      this.animating = false;
      this.allowClick = true;
      this.activeIndex = 0;
      this.loopedSlides = 0;
      this.touch = { touched: false, moved: false };

      this.readParams();
      this.loopCreate();
      this.updateSize();
      this.updateSlides();
      this.slideTo(this.loopedSlides, 0);

      this.onPrev = (e) => { e.preventDefault(); this.slidePrev(); };
      this.onNext = (e) => { e.preventDefault(); this.slideNext(); };
      [[this.prevEl, this.onPrev], [this.nextEl, this.onNext]].forEach(([btn, fn]) => {
        if (!btn) return;
        btn.addEventListener('click', fn);
      });

      this.onTransitionEnd = (e) => { if (e.target === this.track) this.transitionEnd(); };
      this.track.addEventListener('transitionend', this.onTransitionEnd);

      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.addEventListener('pointerdown', this.onDown);
      this.addEventListener('dragstart', (e) => e.preventDefault());
      this.addEventListener('click', (e) => {
        if (this.allowClick) return;
        e.preventDefault();
        if (this.animating) { e.stopPropagation(); e.stopImmediatePropagation(); }
      }, true);
      this.track.addEventListener('click', (e) => {
        const media = e.target.closest('.t28__md');
        if (media && this.track.contains(media)) this.toggleMedia(media);
      });

      this.onResize = () => this.resize();
      window.addEventListener('resize', this.onResize);
      window.addEventListener('orientationchange', this.onResize);
    }

    disconnectedCallback() {
      if (!this.onResize) return;
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      document.removeEventListener('pointermove', this.onMove);
      document.removeEventListener('pointerup', this.onUp);
      document.removeEventListener('pointercancel', this.onUp);
    }

    // ---------------------------------------------------------------- params / layout
    readParams() {
      const d = this.dataset;
      let view = 1;
      let gap = 0;
      if (window.matchMedia(MQ_DESKTOP).matches) {
        gap = parseInt(d.g || 0, 10);
        view = parseFloat(d.v || 3);
      } else if (window.matchMedia(MQ_MOBILE).matches) {
        gap = parseInt(d.gm || 0, 10);
        view = parseFloat(d.vm || 1);
      }
      this.view = view;
      this.gap = gap;
    }

    loopCreate() {
      const real = [...this.track.children].filter((el) => !el.classList.contains('t28__dup'));
      let looped = Math.ceil(parseFloat(this.view));
      if (looped > real.length) looped = real.length;
      this.loopedSlides = looped;
      const append = real.slice(0, looped);
      const prepend = real.slice(real.length - looped);
      append.forEach((el) => {
        const c = el.cloneNode(true);
        c.classList.add('t28__dup');
        this.track.appendChild(c);
      });
      for (let i = prepend.length - 1; i >= 0; i -= 1) {
        const c = prepend[i].cloneNode(true);
        c.classList.add('t28__dup');
        this.track.insertBefore(c, this.track.firstChild);
      }
    }

    loopDestroy() {
      this.track.querySelectorAll('.t28__dup').forEach((el) => el.remove());
    }

    get slides() {
      return [...this.track.children].filter((el) => el.classList.contains('t28__s'));
    }

    updateSize() {
      const cs = getComputedStyle(this);
      const width = this.clientWidth - parseInt(cs.paddingLeft || 0, 10) - parseInt(cs.paddingRight || 0, 10);
      if (width === 0 || Number.isNaN(width)) return;
      this.size = width;
    }

    updateSlides() {
      const { gap, view } = this;
      const size = this.size;
      if (size === undefined) return;
      const slides = this.slides;
      const snapGrid = [];
      const slidesGrid = [];
      let position = 0;
      let virtualSize = -gap;
      const slideSize = (size - (view - 1) * gap) / view;
      slides.forEach((el) => {
        el.style.marginRight = '';
        el.style.width = `${slideSize}px`;
        snapGrid.push(position);
        slidesGrid.push(position);
        position = position + slideSize + gap;
        virtualSize += slideSize + gap;
      });
      virtualSize = Math.max(virtualSize, size);
      let snaps = snapGrid.filter((p) => p <= virtualSize - size);
      if (Math.floor(virtualSize - size) - Math.floor(snaps[snaps.length - 1]) > 1) snaps.push(virtualSize - size);
      if (!snaps.length) snaps = [0];
      if (gap !== 0) slides.forEach((el) => { el.style.marginRight = `${gap}px`; });
      this.slideSize = slideSize;
      this.snapGrid = snaps;
      this.slidesGrid = slidesGrid;
    }

    minTranslate() { return -this.snapGrid[0]; }

    maxTranslate() { return -this.snapGrid[this.snapGrid.length - 1]; }

    setTranslate(x) {
      this.tx = x;
      this.track.style.transform = `translate3d(${x}px, 0px, 0px)`;
    }

    setTransition(ms) {
      this.track.style.transitionDuration = `${ms}ms`;
    }

    getTranslate() {
      const t = getComputedStyle(this.track).transform;
      if (!t || t === 'none') return 0;
      return new DOMMatrixReadOnly(t).m41;
    }

    transitionEnd() {
      clearTimeout(this.animTimer);
      if (!this.animating) return;
      this.animating = false;
    }

    // ---------------------------------------------------------------- navigation
    slideTo(index, speed = SPEED) {
      let slideIndex = Math.max(0, index);
      const { snapGrid, slidesGrid } = this;
      if (!snapGrid) return false;
      let snapIndex = slideIndex;
      if (snapIndex >= snapGrid.length) snapIndex = snapGrid.length - 1;
      const translate = -snapGrid[snapIndex];
      for (let i = 0; i < slidesGrid.length; i += 1) {
        const nt = -Math.floor(translate * 100);
        const g = Math.floor(slidesGrid[i] * 100);
        const gn = Math.floor(slidesGrid[i + 1] * 100);
        if (typeof slidesGrid[i + 1] !== 'undefined') {
          if (nt >= g && nt < gn - (gn - g) / 2) slideIndex = i;
          else if (nt >= g && nt < gn) slideIndex = i + 1;
        } else if (nt >= g) slideIndex = i;
      }
      if (translate === this.tx) {
        this.activeIndex = slideIndex;
        return false;
      }
      this.activeIndex = slideIndex;
      if (speed === 0) {
        this.setTransition(0);
        this.setTranslate(translate);
      } else {
        this.setTransition(speed);
        this.setTranslate(translate);
        if (!this.animating) {
          this.animating = true;
          clearTimeout(this.animTimer);
          // transitionend never fires when transitions are disabled; don't stay locked.
          this.animTimer = setTimeout(() => this.transitionEnd(), speed + 100);
        }
      }
      return true;
    }

    loopFix() {
      const { activeIndex, loopedSlides } = this;
      const count = this.slides.length;
      const diff = -this.snapGrid[activeIndex] - this.getTranslate();
      let newIndex;
      if (activeIndex < loopedSlides) newIndex = count - loopedSlides * 3 + activeIndex + loopedSlides;
      else if (activeIndex >= count - loopedSlides) newIndex = -count + activeIndex + loopedSlides + loopedSlides;
      else return;
      const changed = this.slideTo(newIndex, 0);
      if (changed && diff !== 0) this.setTranslate(this.tx - diff);
    }

    slideNext() {
      if (this.animating) return false;
      this.loopFix();
      this.clientLeftCache = this.track.clientLeft;
      return this.slideTo(this.activeIndex + 1);
    }

    slidePrev() {
      if (this.animating) return false;
      this.loopFix();
      this.clientLeftCache = this.track.clientLeft;
      const norm = (v) => (v < 0 ? -Math.floor(Math.abs(v)) : Math.floor(v));
      const nt = norm(-this.tx);
      const prevSnap = this.snapGrid[this.snapGrid.map(norm).indexOf(nt) - 1];
      let prevIndex = 0;
      if (typeof prevSnap !== 'undefined') {
        prevIndex = this.slidesGrid.indexOf(prevSnap);
        if (prevIndex < 0) prevIndex = this.activeIndex - 1;
      }
      return this.slideTo(prevIndex);
    }

    resize() {
      if (this.offsetWidth === 0) return;
      const prevView = this.view;
      const prevLooped = this.loopedSlides;
      const active = this.activeIndex;
      this.readParams();
      if (this.view !== prevView) {
        this.loopDestroy();
        this.loopCreate();
        this.updateSize();
        this.updateSlides();
        this.slideTo(active - prevLooped + this.loopedSlides, 0);
      }
      this.updateSize();
      this.updateSlides();
      this.tx = NaN;
      this.slideTo(this.activeIndex, 0);
    }

    // ---------------------------------------------------------------- dragging (Swiper 6 semantics)
    onDown(e) {
      const t = this.touch;
      if (e.pointerType === 'mouse' && e.button > 0) return;
      if (t.touched && t.moved) return;
      Object.assign(t, {
        touched: true, moved: false, scrolling: undefined, startMoving: undefined,
        startX: e.pageX, startY: e.pageY, startTime: Date.now(), diff: 0, id: e.pointerId,
      });
      this.allowClick = true;
      this.updateSize();
      this.swipeDirection = undefined;
      if (e.pointerType !== 'touch') {
        const target = e.target;
        if (!(target.matches && target.matches(FOCUSABLE))) e.preventDefault();
        const active = document.activeElement;
        if (active && active !== target && active.matches && active.matches(FOCUSABLE)) active.blur();
      }
      document.addEventListener('pointermove', this.onMove, { passive: false });
      document.addEventListener('pointerup', this.onUp);
      document.addEventListener('pointercancel', this.onUp);
    }

    onMove(e) {
      const t = this.touch;
      if (!t.touched || e.pointerId !== t.id) return;
      const diffX = e.pageX - t.startX;
      const diffY = e.pageY - t.startY;
      if (typeof t.scrolling === 'undefined') {
        if (e.pageY === t.startY) t.scrolling = false;
        else if (diffX * diffX + diffY * diffY >= 25) {
          t.scrolling = (Math.atan2(Math.abs(diffY), Math.abs(diffX)) * 180) / Math.PI > 45;
        }
      }
      if (typeof t.startMoving === 'undefined' && (e.pageX !== t.startX || e.pageY !== t.startY)) t.startMoving = true;
      if (t.scrolling) { t.touched = false; return; }
      if (!t.startMoving) return;
      this.allowClick = false;
      if (e.cancelable) e.preventDefault();
      if (!t.moved) {
        this.loopFix();
        t.startTranslate = this.getTranslate();
        this.setTransition(0);
        if (this.animating) this.transitionEnd();
      }
      t.moved = true;
      const diff = diffX;
      t.diff = diff;
      this.swipeDirection = diff > 0 ? 'prev' : 'next';
      let current = diff + t.startTranslate;
      if (diff > 0 && current > this.minTranslate()) {
        current = this.minTranslate() - 1 + (-this.minTranslate() + t.startTranslate + diff) ** 0.85;
      } else if (diff < 0 && current < this.maxTranslate()) {
        current = this.maxTranslate() + 1 - (this.maxTranslate() - t.startTranslate - diff) ** 0.85;
      }
      t.currentTranslate = current;
      this.setTranslate(current);
    }

    onUp(e) {
      const t = this.touch;
      if (e.pointerId !== t.id) return;
      document.removeEventListener('pointermove', this.onMove);
      document.removeEventListener('pointerup', this.onUp);
      document.removeEventListener('pointercancel', this.onUp);
      if (!t.touched) { t.moved = false; t.startMoving = false; return; }
      const timeDiff = Date.now() - t.startTime;
      setTimeout(() => { this.allowClick = true; }, 0);
      if (!t.moved || !this.swipeDirection || t.diff === 0 || t.currentTranslate === t.startTranslate) {
        t.touched = false; t.moved = false; t.startMoving = false;
        return;
      }
      t.touched = false; t.moved = false; t.startMoving = false;
      const currentPos = -this.tx;
      const { slidesGrid } = this;
      let stopIndex = 0;
      let groupSize = this.slideSize;
      for (let i = 0; i < slidesGrid.length; i += 1) {
        if (typeof slidesGrid[i + 1] !== 'undefined') {
          if (currentPos >= slidesGrid[i] && currentPos < slidesGrid[i + 1]) {
            stopIndex = i;
            groupSize = slidesGrid[i + 1] - slidesGrid[i];
          }
        } else if (currentPos >= slidesGrid[i]) {
          stopIndex = i;
          groupSize = slidesGrid[slidesGrid.length - 1] - slidesGrid[slidesGrid.length - 2];
        }
      }
      const ratio = (currentPos - slidesGrid[stopIndex]) / groupSize;
      if (timeDiff > 300) {
        if (this.swipeDirection === 'next') this.slideTo(ratio >= 0.5 ? stopIndex + 1 : stopIndex);
        else this.slideTo(ratio > 0.5 ? stopIndex + 1 : stopIndex);
      } else if (this.swipeDirection === 'next') this.slideTo(stopIndex + 1);
      else this.slideTo(stopIndex);
    }

    // ---------------------------------------------------------------- video toggles
    toggleMedia(item) {
      const all = this.track.querySelectorAll('.t28__md');
      all.forEach((other) => {
        if (other === item) return;
        const v = other.querySelector('video');
        const f = other.querySelector('iframe');
        const btn = other.querySelector('.t28__pl');
        if (v) { v.pause(); v.controls = false; }
        if (f) { const src = f.getAttribute('data-src2'); if (src && f.getAttribute('src') !== src) f.src = src; }
        if (btn) btn.classList.add('active');
      });
      const button = item.querySelector('.t28__pl');
      if (!button) return;
      const video = item.querySelector('video');
      const iframe = item.querySelector('iframe');
      setTimeout(() => {
        if (button.classList.contains('active')) {
          if (video) { const p = video.play(); if (p && p.catch) p.catch(() => {}); video.controls = true; }
          if (iframe) iframe.src = iframe.getAttribute('data-src1');
        } else {
          if (video) { video.pause(); video.controls = false; }
          if (iframe) iframe.src = iframe.getAttribute('data-src2');
        }
        button.classList.toggle('active');
      }, 400);
    }
  }

  customElements.define('t28-slider', T28Slider);
})();
